import dayjs, { Dayjs } from 'dayjs';

/**
 * Shared date-range guards for the whole app. The rules, applied everywhere a
 * from/to range or a past-dated field is picked:
 *   • neither end may be in the FUTURE (after today), and
 *   • the TO date may not be before the FROM date.
 *
 * AntD `DatePicker.RangePicker` already enforces to ≥ from natively, so for a
 * range picker you only need `disabledDate={disableFuture}`. For a pair of
 * separate `DatePicker`s, use `disableFromDate(to)` / `disableToDate(from)` so
 * the two stay mutually constrained.
 *
 * Deliberately NOT used for genuinely future-dated inputs (event scheduling,
 * territory-coverage validity windows, invoice due dates) — those allow the future.
 */

const todayEnd = () => dayjs().endOf('day');

/** Today as 'YYYY-MM-DD' — for the `max`/`min` attributes of native <input type="date">. */
export const todayStr = () => dayjs().format('YYYY-MM-DD');

/** AntD `disabledDate`: block any day after today (no future). */
export const disableFuture = (current: Dayjs) => !!current && current.isAfter(todayEnd());

/** `disabledDate` for the FROM picker of a separate from/to pair: no future, and not after the chosen TO. */
export const disableFromDate = (to?: Dayjs | string | null) => (current: Dayjs) => {
  if (!current) return false;
  if (current.isAfter(todayEnd())) return true;
  if (to) {
    const t = dayjs(to);
    if (t.isValid() && current.isAfter(t.endOf('day'))) return true;
  }
  return false;
};

/** `disabledDate` for the TO picker of a separate from/to pair: no future, and not before the chosen FROM. */
export const disableToDate = (from?: Dayjs | string | null) => (current: Dayjs) => {
  if (!current) return false;
  if (current.isAfter(todayEnd())) return true;
  if (from) {
    const f = dayjs(from);
    if (f.isValid() && current.isBefore(f.startOf('day'))) return true;
  }
  return false;
};
